import React, { useEffect, useMemo, useRef, useState } from "react";

const VOICE_FIELDS = [
  {
    key: "patient_name",
    hinglish: "Patient ka naam kya hai?",
    hindi: "मरीज़ का नाम क्या है?",
  },
  {
    key: "age",
    hinglish: "Patient ki age kya hai?",
    hindi: "मरीज़ की उम्र कितनी है?",
  },
  {
    key: "gender",
    hinglish: "Gender kya hai?",
    hindi: "लिंग क्या है? पुरुष, महिला या अन्य?",
  },
  {
    key: "relation",
    hinglish: "Patient aapse kya relation mein hai?",
    hindi: "मरीज़ आपसे किस रिश्ते में है?",
  },
  {
    key: "symptoms",
    hinglish: "Symptoms bataiye. Kya problem ho rahi hai?",
    hindi: "कृपया लक्षण बताइए। क्या समस्या हो रही है?",
  },
  {
    key: "duration",
    hinglish: "Ye symptoms kab se hain?",
    hindi: "ये लक्षण कब से हैं?",
  },
  {
    key: "severity",
    hinglish: "Symptoms mild, moderate ya severe hain?",
    hindi: "लक्षण हल्के हैं, मध्यम हैं या गंभीर हैं?",
  },
  {
    key: "diet",
    hinglish: "Diet pattern kaisa hai?",
    hindi: "खाने-पीने की आदत कैसी है?",
  },
  {
    key: "current_medications",
    hinglish: "Abhi koi medicines chal rahi hain?",
    hindi: "क्या अभी कोई दवाइयाँ चल रही हैं?",
  },
  {
    key: "currently_suffering_from",
    hinglish: "Abhi patient kis condition se suffer kar raha hai?",
    hindi: "अभी मरीज़ किस बीमारी या समस्या से जूझ रहा है?",
  },
  {
    key: "earlier_disease",
    hinglish: "Past mein koi disease ya illness rahi hai?",
    hindi: "पहले कोई बीमारी या पुरानी समस्या रही है?",
  },
  {
    key: "past_surgery",
    hinglish: "Koi surgery history hai?",
    hindi: "क्या पहले कभी कोई सर्जरी हुई है?",
  },
  {
    key: "past_test_history",
    hinglish: "Koi previous test, scan ya blood report history hai?",
    hindi: "क्या कोई पुरानी जांच, स्कैन या ब्लड रिपोर्ट की जानकारी है?",
  },
  {
    key: "family_history_major_illnesses",
    hinglish: "Family history mein koi major illness hai?",
    hindi: "परिवार में किसी बड़ी बीमारी का इतिहास है?",
  },
];

const LANGUAGE_OPTIONS = [
  { value: "hi-IN", label: "हिंदी" },
  { value: "en-IN", label: "Hinglish / English" },
  { value: "en-US", label: "English US" },
  { value: "en-GB", label: "English UK" },
];

export default function VoiceLiveQuestionnaire({
  sessionId,
  initialData,
  onComplete,
}) {
  const [fieldIndex, setFieldIndex] = useState(0);
  const [answers, setAnswers] = useState(initialData || {});
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");

  const [recording, setRecording] = useState(false);
  const [assistantSpeaking, setAssistantSpeaking] = useState(false);
  const [autoMode, setAutoMode] = useState(false);
  const [conversationStarted, setConversationStarted] = useState(false);

  const [sourceLanguage, setSourceLanguage] = useState("hi-IN");
  const [voicesReady, setVoicesReady] = useState(false);
  const [lastTranscript, setLastTranscript] = useState("");
  const [micError, setMicError] = useState("");

  const recognitionRef = useRef(null);
  const bottomRef = useRef(null);
  const autoModeRef = useRef(false);
  const speakingRef = useRef(false);
  const fieldIndexRef = useRef(0);
  const answersRef = useRef(initialData || {});
  const completedRef = useRef(false);

  const currentField = useMemo(() => VOICE_FIELDS[fieldIndex], [fieldIndex]);

  const speechSupported =
    typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);

  useEffect(() => {
    autoModeRef.current = autoMode;
  }, [autoMode]);

  useEffect(() => {
    fieldIndexRef.current = fieldIndex;
  }, [fieldIndex]);

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  const getQuestionText = (field) => {
    if (sourceLanguage === "hi-IN") return field.hindi;
    return field.hinglish;
  };

  const getPreferredVoice = () => {
    const voices = window.speechSynthesis?.getVoices?.() || [];

    if (sourceLanguage === "hi-IN") {
      return (
        voices.find(
          (v) =>
            v.lang === "hi-IN" &&
            /google|hindi|हिन्दी|lekha|sangeeta|veena/i.test(v.name)
        ) ||
        voices.find((v) => v.lang === "hi-IN") ||
        voices.find((v) => /hindi|हिन्दी/i.test(v.name)) ||
        voices.find((v) => v.lang?.startsWith("hi")) ||
        null
      );
    }

    return (
      voices.find((v) => v.lang === "en-IN") ||
      voices.find((v) => /india|indian/i.test(v.name)) ||
      voices.find((v) => /female|zira|samantha|susan/i.test(v.name)) ||
      voices[0] ||
      null
    );
  };

  const normalizeAnswer = (key, text) => {
    const value = text.trim().toLowerCase();

    if (key === "gender") {
      if (
        ["mail", "mael", "male", "मेल", "पुरुष", "लड़का", "आदमी"].includes(
          value
        )
      ) {
        return "Male";
      }

      if (
        [
          "female",
          "femail",
          "fe male",
          "फीमेल",
          "महिला",
          "स्त्री",
          "लड़की",
        ].includes(value)
      ) {
        return "Female";
      }

      if (["other", "others", "अन्य"].includes(value)) return "Other";
    }

    if (key === "severity") {
      if (["mild", "हल्का", "हल्की", "kam", "कम"].includes(value)) {
        return "Mild";
      }

      if (["moderate", "medium", "मध्यम", "बीच का"].includes(value)) {
        return "Moderate";
      }

      if (["severe", "गंभीर", "bahut zyada", "बहुत ज्यादा"].includes(value)) {
        return "Severe";
      }
    }

    if (key === "age") {
      const number = value.match(/\d+/);
      if (number) return number[0];
    }

    return text.trim();
  };

  useEffect(() => {
    if (!window.speechSynthesis) return;

    const loadVoices = () => {
      window.speechSynthesis.getVoices();
      setVoicesReady(true);
    };

    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;

    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);

  useEffect(() => {
    const nextAnswers = initialData || {};
    setAnswers(nextAnswers);
    answersRef.current = nextAnswers;
  }, [initialData]);

  useEffect(() => {
    if (conversationStarted) return;

    const firstQuestion =
      sourceLanguage === "hi-IN" ? VOICE_FIELDS[0].hindi : VOICE_FIELDS[0].hinglish;

    const intro =
      sourceLanguage === "hi-IN"
        ? "नमस्ते। मैं आपकी वॉइस मेडिकल इंटेक असिस्टेंट हूँ। शुरू करने के लिए Start Voice Intake दबाएँ।"
        : "Namaste. I am your voice medical intake assistant. Press Start Voice Intake to begin.";

    setMessages([
      { role: "assistant", content: intro },
      { role: "assistant", content: firstQuestion },
    ]);
  }, [sourceLanguage, conversationStarted]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, recording, assistantSpeaking, lastTranscript]);

  useEffect(() => {
    return () => {
      stopRecognition();
      window.speechSynthesis?.cancel?.();
    };
  }, []);

  const stopRecognition = () => {
    try {
      recognitionRef.current?.stop?.();
    } catch {}
    recognitionRef.current = null;
    setRecording(false);
  };

  const speakText = (text, afterSpeak) => {
    if (!window.speechSynthesis) {
      afterSpeak?.();
      return;
    }

    stopRecognition();

    const utterance = new SpeechSynthesisUtterance(text);
    const voice = getPreferredVoice();

    if (voice) {
      utterance.voice = voice;
      utterance.lang = voice.lang || sourceLanguage;
    } else {
      utterance.lang = sourceLanguage;
    }

    utterance.rate = sourceLanguage === "hi-IN" ? 0.86 : 0.92;
    utterance.pitch = sourceLanguage === "hi-IN" ? 1.0 : 1.05;
    utterance.volume = 1;

    utterance.onstart = () => {
      speakingRef.current = true;
      setAssistantSpeaking(true);
    };

    utterance.onend = () => {
      speakingRef.current = false;
      setAssistantSpeaking(false);
      afterSpeak?.();
    };

    utterance.onerror = () => {
      speakingRef.current = false;
      setAssistantSpeaking(false);
      afterSpeak?.();
    };

    window.speechSynthesis.cancel();

    setTimeout(() => {
      window.speechSynthesis.speak(utterance);
    }, 140);
  };

  const askCurrentQuestion = (index = fieldIndexRef.current) => {
    const question = getQuestionText(VOICE_FIELDS[index]);

    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last?.role === "assistant" && last?.content === question) return prev;
      return [...prev, { role: "assistant", content: question }];
    });

    speakText(question, () => {
      if (autoModeRef.current && !completedRef.current) {
        setTimeout(() => startVoice(true), 300);
      }
    });
  };

  const goNext = (answerText) => {
    const activeIndex = fieldIndexRef.current;
    const activeField = VOICE_FIELDS[activeIndex];
    const cleanAnswer = normalizeAnswer(activeField.key, answerText || "");

    if (!cleanAnswer) return;

    stopRecognition();

    const nextAnswers = {
      ...answersRef.current,
      [activeField.key]: cleanAnswer,
    };

    answersRef.current = nextAnswers;
    setAnswers(nextAnswers);
    setLastTranscript("");

    setMessages((prev) => [...prev, { role: "user", content: cleanAnswer }]);
    setInput("");

    if (activeIndex < VOICE_FIELDS.length - 1) {
      const nextIndex = activeIndex + 1;
      fieldIndexRef.current = nextIndex;
      setFieldIndex(nextIndex);

      setTimeout(() => {
        askCurrentQuestion(nextIndex);
      }, 420);
    } else {
      completedRef.current = true;
      setAutoMode(false);
      autoModeRef.current = false;

      const doneText =
        sourceLanguage === "hi-IN"
          ? "प्रश्नावली पूरी हो गई है। अब मैं आपके जवाब सेव कर रहा हूँ।"
          : "Questionnaire complete. I am now saving your answers.";

      setTimeout(() => {
        setMessages((prev) => [...prev, { role: "assistant", content: doneText }]);
        speakText(doneText, () => onComplete(nextAnswers));
      }, 420);
    }
  };

  const startVoice = (silent = false) => {
    if (!speechSupported) {
      alert("Voice input Chrome, Edge, ya Brave mein best kaam karta hai.");
      return;
    }

    if (assistantSpeaking || speakingRef.current) return;

    setMicError("");

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    const recognition = new SpeechRecognition();
    recognition.lang = sourceLanguage;
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;

    recognitionRef.current = recognition;

    let localFinalText = "";

    recognition.onstart = () => {
      setRecording(true);
      if (!silent) setLastTranscript("");
    };

    recognition.onresult = (event) => {
      let finalText = "";
      let interimText = "";

      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const transcript = event.results[i][0]?.transcript || "";

        if (event.results[i].isFinal) {
          finalText += transcript;
        } else {
          interimText += transcript;
        }
      }

      const visibleText = (finalText || interimText || "").trim();
      setInput(visibleText);
      setLastTranscript(visibleText);

      if (finalText.trim()) {
        localFinalText = finalText.trim();
      }
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event);
      setRecording(false);

      if (event.error === "not-allowed") {
        setMicError("Microphone permission blocked. Browser settings mein mic allow karein.");
        setAutoMode(false);
        autoModeRef.current = false;
      } else if (event.error === "no-speech") {
        setMicError(
          sourceLanguage === "hi-IN"
            ? "आवाज़ साफ़ नहीं आई। दोबारा बोलें या टाइप करें।"
            : "Voice was not clear. Speak again or type your answer."
        );
      } else {
        setMicError(`Voice error: ${event.error}`);
      }
    };

    recognition.onend = () => {
      setRecording(false);

      if (localFinalText.trim()) {
        setTimeout(() => {
          goNext(localFinalText.trim());
        }, 280);
        return;
      }

      if (autoModeRef.current && !completedRef.current && !speakingRef.current) {
        setTimeout(() => {
          if (!input.trim()) {
            startVoice(true);
          }
        }, 900);
      }
    };

    try {
      recognition.start();
    } catch (e) {
      console.error(e);
      setRecording(false);
    }
  };

  const startConversation = () => {
    completedRef.current = false;
    setConversationStarted(true);
    setAutoMode(true);
    autoModeRef.current = true;
    setMicError("");

    const intro =
      sourceLanguage === "hi-IN"
        ? "नमस्ते। मैं MedGen वॉइस असिस्टेंट हूँ। मैं एक-एक करके सवाल पूछूँगी। कृपया साफ़ आवाज़ में जवाब दें।"
        : "Namaste. I am the MedGen voice assistant. I will ask one question at a time. Please answer clearly.";

    const question = getQuestionText(VOICE_FIELDS[fieldIndexRef.current]);

    setMessages([
      { role: "assistant", content: intro },
      { role: "assistant", content: question },
    ]);

    speakText(`${intro} ${question}`, () => {
      if (autoModeRef.current) {
        setTimeout(() => startVoice(true), 350);
      }
    });
  };

  const pauseConversation = () => {
    setAutoMode(false);
    autoModeRef.current = false;
    stopRecognition();
    window.speechSynthesis?.cancel?.();
    speakingRef.current = false;
    setAssistantSpeaking(false);
  };

  const resumeConversation = () => {
    setAutoMode(true);
    autoModeRef.current = true;
    askCurrentQuestion(fieldIndexRef.current);
  };

  const handleSubmit = () => {
    goNext(input);
  };

  const skipQuestion = () => {
    stopRecognition();

    setMessages((prev) => [
      ...prev,
      { role: "user", content: sourceLanguage === "hi-IN" ? "छोड़ें" : "Skip" },
    ]);

    setInput("");
    setLastTranscript("");

    if (fieldIndex < VOICE_FIELDS.length - 1) {
      const nextIndex = fieldIndex + 1;
      fieldIndexRef.current = nextIndex;
      setFieldIndex(nextIndex);

      setTimeout(() => {
        askCurrentQuestion(nextIndex);
      }, 300);
    } else {
      completedRef.current = true;
      setAutoMode(false);
      autoModeRef.current = false;
      onComplete(answersRef.current);
    }
  };

  const repeatQuestion = () => {
    askCurrentQuestion(fieldIndexRef.current);
  };

  const progress = Math.round(((fieldIndex + 1) / VOICE_FIELDS.length) * 100);

  const avatarState = assistantSpeaking
    ? "speaking"
    : recording
    ? "listening"
    : autoMode
    ? "ready"
    : "idle";

  return (
    <div className="mgvoice-shell">
      <style>{voiceCss}</style>

      <section className="mgvoice-hero">
        <div className="mgvoice-avatar-card">
          <div className={`mgvoice-avatar ${avatarState}`}>
            <div className="mgvoice-avatar-halo" />
            <div className="mgvoice-face">
              <div className="mgvoice-eye left" />
              <div className="mgvoice-eye right" />
              <div className="mgvoice-nose" />
              <div className="mgvoice-mouth" />
            </div>
            <div className="mgvoice-neck" />
          </div>

          <div className="mgvoice-avatar-status">
            <span>MedGen Voice Assistant</span>
            <strong>
              {assistantSpeaking
                ? "Asking question"
                : recording
                ? "Listening now"
                : autoMode
                ? "Auto intake active"
                : "Ready to begin"}
            </strong>
            <p>
              {sourceLanguage === "hi-IN"
                ? "वर्चुअल असिस्टेंट सवाल पूछेगा, बोलेगा और जवाब अपने-आप सुनेगा।"
                : "Virtual assistant asks, speaks, and listens automatically after start."}
            </p>
          </div>

          <div className="mgvoice-wave" aria-hidden="true">
            <i />
            <i />
            <i />
            <i />
            <i />
          </div>
        </div>

        <div className="mgvoice-control-card">
          <div className="mgvoice-header">
            <div>
              <div className="mgvoice-kicker">Real-time Voice Intake</div>
              <h3>Live Medical Questionnaire</h3>
              <p>
                {sourceLanguage === "hi-IN"
                  ? "हिंदी में बोलकर या टाइप करके जवाब दें।"
                  : "Speak or type to answer. The assistant will continue question by question."}
              </p>
            </div>

            <select
              value={sourceLanguage}
              onChange={(e) => {
                pauseConversation();
                setSourceLanguage(e.target.value);
                setFieldIndex(0);
                fieldIndexRef.current = 0;
                completedRef.current = false;
              }}
              className="mgvoice-lang-select"
            >
              {LANGUAGE_OPTIONS.map((lang) => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>

          <div className="mgvoice-progress">
            <div>
              <span>
                Question {fieldIndex + 1} / {VOICE_FIELDS.length}
              </span>
              <strong>{currentField?.key?.replaceAll("_", " ")}</strong>
            </div>

            <b>{progress}%</b>
          </div>

          <div className="mgvoice-progress-track">
            <i style={{ width: `${progress}%` }} />
          </div>

          <div className="mgvoice-actions">
            {!conversationStarted || !autoMode ? (
              <button
                type="button"
                className="mgvoice-btn primary"
                onClick={conversationStarted ? resumeConversation : startConversation}
              >
                {conversationStarted ? "Resume Voice Intake" : "Start Voice Intake"}
              </button>
            ) : (
              <button
                type="button"
                className="mgvoice-btn danger"
                onClick={pauseConversation}
              >
                Pause Intake
              </button>
            )}

            <button
              type="button"
              className="mgvoice-btn soft"
              onClick={repeatQuestion}
            >
              🔊 Repeat
            </button>

            <button
              type="button"
              className="mgvoice-btn ghost"
              onClick={skipQuestion}
            >
              Skip
            </button>
          </div>

          {micError ? <div className="mgvoice-alert">{micError}</div> : null}

          {!speechSupported && (
            <div className="mgvoice-alert">
              Voice input is not supported in this browser. Chrome, Edge, or Brave use karein.
            </div>
          )}

          {sourceLanguage === "hi-IN" && voicesReady && !getPreferredVoice() && (
            <div className="mgvoice-alert">
              Hindi voice is not installed on this device. Browser fallback voice may sound unnatural.
            </div>
          )}
        </div>
      </section>

      <section className="mgvoice-chat-card">
        <div className="mgvoice-chat-window">
          {messages.map((message, index) => (
            <div key={`${message.role}-${index}`} className={`mgvoice-msg ${message.role}`}>
              <div className="mgvoice-bubble">{message.content}</div>
            </div>
          ))}

          {recording && (
            <div className="mgvoice-msg assistant">
              <div className="mgvoice-bubble listening">
                {sourceLanguage === "hi-IN" ? "सुन रहा हूँ..." : "Listening..."}
              </div>
            </div>
          )}

          {lastTranscript && recording && (
            <div className="mgvoice-live-transcript">
              <span>Live transcript</span>
              <strong>{lastTranscript}</strong>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        <div className="mgvoice-input-row">
          <button
            type="button"
            className={`mgvoice-mic-btn ${recording ? "recording" : ""}`}
            onClick={recording ? stopRecognition : () => startVoice(false)}
            title={recording ? "Stop listening" : "Manual listen"}
          >
            {recording ? "■" : "🎙️"}
          </button>

          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              sourceLanguage === "hi-IN"
                ? "यहाँ जवाब लिखें या Start Voice Intake दबाएँ..."
                : "Type answer here or press Start Voice Intake..."
            }
            rows={1}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />

          <button type="button" className="mgvoice-btn primary compact" onClick={handleSubmit}>
            Send
          </button>
        </div>
      </section>
    </div>
  );
}

const voiceCss = `
.mgvoice-shell {
  --mg-black: #03060c;
  --mg-navy: #06101f;
  --mg-blue: #1f7bc1;
  --mg-cyan: #18b7ff;
  --mg-text: #07111f;
  --mg-muted: #66778e;
  --mg-border: #cddcec;

  width: 100%;
  color: var(--mg-text);
}

.mgvoice-shell * {
  box-sizing: border-box;
}

.mgvoice-hero {
  display: grid;
  grid-template-columns: 380px minmax(0, 1fr);
  gap: 20px;
  margin-bottom: 18px;
}

.mgvoice-avatar-card,
.mgvoice-control-card,
.mgvoice-chat-card {
  border-radius: 24px;
  border: 1px solid var(--mg-border);
  box-shadow: 0 14px 38px rgba(7, 17, 31, 0.06);
}

.mgvoice-avatar-card {
  position: relative;
  overflow: hidden;
  min-height: 440px;
  padding: 26px;
  display: grid;
  place-items: center;
  color: white;
  background:
    radial-gradient(circle at 50% 14%, rgba(24, 183, 255, 0.26), transparent 32%),
    radial-gradient(circle at 15% 95%, rgba(31, 123, 193, 0.22), transparent 36%),
    linear-gradient(145deg, #03060c, #081525 62%, #06101f);
  border-color: rgba(24, 183, 255, 0.18);
}

.mgvoice-avatar-card::before {
  content: "";
  position: absolute;
  inset: 20px;
  border-radius: 22px;
  border: 1px solid rgba(24, 183, 255, 0.12);
  pointer-events: none;
}

.mgvoice-avatar {
  position: relative;
  width: 190px;
  height: 230px;
  display: grid;
  place-items: center;
}

.mgvoice-avatar-halo {
  position: absolute;
  width: 210px;
  height: 210px;
  border-radius: 50%;
  background:
    radial-gradient(circle, rgba(24,183,255,0.24), transparent 58%);
  animation: mgvoiceHalo 2.4s ease-in-out infinite;
}

.mgvoice-face {
  position: relative;
  width: 150px;
  height: 170px;
  border-radius: 54px 54px 62px 62px;
  background:
    radial-gradient(circle at 30% 20%, rgba(255,255,255,0.32), transparent 22%),
    linear-gradient(145deg, #e9f7ff 0%, #b9def7 46%, #78b9e8 100%);
  border: 1px solid rgba(255,255,255,0.38);
  box-shadow:
    inset 0 -18px 35px rgba(31, 123, 193, 0.18),
    0 28px 65px rgba(24, 183, 255, 0.18);
  animation: mgvoiceFloat 3.2s ease-in-out infinite;
}

.mgvoice-face::before {
  content: "";
  position: absolute;
  left: 50%;
  top: -18px;
  width: 82px;
  height: 38px;
  transform: translateX(-50%);
  border-radius: 999px 999px 18px 18px;
  background: linear-gradient(135deg, #07111f, #0b2b47);
  box-shadow: 0 8px 18px rgba(3,6,12,0.2);
}

.mgvoice-eye {
  position: absolute;
  top: 68px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background:
    radial-gradient(circle at 36% 34%, #ffffff 0 18%, transparent 19%),
    #06101f;
  box-shadow: 0 0 14px rgba(24, 183, 255, 0.34);
  animation: mgvoiceBlink 4.8s infinite;
}

.mgvoice-eye.left {
  left: 42px;
}

.mgvoice-eye.right {
  right: 42px;
}

.mgvoice-nose {
  position: absolute;
  left: 50%;
  top: 92px;
  width: 12px;
  height: 24px;
  transform: translateX(-50%);
  border-radius: 999px;
  background: rgba(31, 123, 193, 0.32);
}

.mgvoice-mouth {
  position: absolute;
  left: 50%;
  bottom: 42px;
  width: 42px;
  height: 12px;
  transform: translateX(-50%);
  border-radius: 999px;
  background: #06101f;
  box-shadow: 0 0 12px rgba(24, 183, 255, 0.28);
}

.mgvoice-avatar.speaking .mgvoice-mouth {
  animation: mgvoiceTalk 0.42s ease-in-out infinite;
}

.mgvoice-avatar.listening .mgvoice-face {
  box-shadow:
    inset 0 -18px 35px rgba(31, 123, 193, 0.18),
    0 0 0 10px rgba(24, 183, 255, 0.08),
    0 28px 65px rgba(24, 183, 255, 0.28);
}

.mgvoice-avatar.listening .mgvoice-avatar-halo,
.mgvoice-avatar.speaking .mgvoice-avatar-halo {
  animation: mgvoiceHaloActive 1.2s ease-in-out infinite;
}

.mgvoice-neck {
  position: absolute;
  bottom: 18px;
  width: 76px;
  height: 42px;
  border-radius: 22px 22px 12px 12px;
  background: linear-gradient(180deg, #91cbed, #508fc1);
  z-index: -1;
}

.mgvoice-avatar-status {
  position: relative;
  z-index: 2;
  text-align: center;
  margin-top: 12px;
}

.mgvoice-avatar-status span {
  display: block;
  margin-bottom: 8px;
  color: #9fdcff;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.mgvoice-avatar-status strong {
  display: block;
  color: white;
  font-size: 23px;
  font-weight: 950;
  letter-spacing: -0.045em;
}

.mgvoice-avatar-status p {
  max-width: 290px;
  margin: 10px auto 0;
  color: #a9bdd3;
  font-size: 13px;
  line-height: 1.55;
}

.mgvoice-wave {
  position: relative;
  z-index: 2;
  height: 54px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}

.mgvoice-wave i {
  width: 8px;
  height: 18px;
  border-radius: 999px;
  background: linear-gradient(180deg, #18b7ff, #1f7bc1);
  animation: mgvoiceWave 1s ease-in-out infinite;
}

.mgvoice-wave i:nth-child(2) {
  animation-delay: 0.12s;
}

.mgvoice-wave i:nth-child(3) {
  animation-delay: 0.24s;
}

.mgvoice-wave i:nth-child(4) {
  animation-delay: 0.36s;
}

.mgvoice-wave i:nth-child(5) {
  animation-delay: 0.48s;
}

.mgvoice-control-card {
  padding: 26px;
  background:
    radial-gradient(circle at 96% 0%, rgba(24, 183, 255, 0.10), transparent 34%),
    linear-gradient(180deg, #ffffff 0%, #f7fbff 100%);
}

.mgvoice-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  margin-bottom: 22px;
}

.mgvoice-kicker {
  margin-bottom: 10px;
  color: #125a94;
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.mgvoice-header h3 {
  margin: 0;
  color: var(--mg-text);
  font-size: clamp(28px, 3vw, 42px);
  line-height: 1.04;
  letter-spacing: -0.05em;
  font-weight: 950;
}

.mgvoice-header p {
  max-width: 620px;
  margin: 12px 0 0;
  color: var(--mg-muted);
  font-size: 15px;
  line-height: 1.65;
}

.mgvoice-lang-select {
  min-height: 42px;
  padding: 9px 12px;
  border-radius: 12px;
  border: 1px solid var(--mg-border);
  background: #ffffff;
  color: var(--mg-text);
  font-size: 13px;
  font-weight: 850;
  outline: none;
}

.mgvoice-progress {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: center;
  padding: 16px;
  border-radius: 16px;
  background: #eef6ff;
  border: 1px solid #d9e8f6;
}

.mgvoice-progress span {
  display: block;
  margin-bottom: 5px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.mgvoice-progress strong {
  color: var(--mg-text);
  font-size: 15px;
  font-weight: 950;
  text-transform: capitalize;
}

.mgvoice-progress b {
  color: #0b4e84;
  font-size: 20px;
  font-weight: 950;
}

.mgvoice-progress-track {
  height: 9px;
  overflow: hidden;
  margin: 12px 0 18px;
  border-radius: 999px;
  background: #dfeaf6;
}

.mgvoice-progress-track i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #1f7bc1, #18b7ff);
  box-shadow: 0 0 18px rgba(24, 183, 255, 0.28);
  transition: width 0.25s ease;
}

.mgvoice-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.mgvoice-btn {
  min-height: 42px;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 950;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: 0.16s ease;
  white-space: nowrap;
}

.mgvoice-btn.primary {
  color: white;
  background: linear-gradient(135deg, #1f7bc1, #0f5f9d);
  box-shadow: 0 14px 28px rgba(31, 123, 193, 0.22);
}

.mgvoice-btn.danger {
  color: white;
  background: linear-gradient(135deg, #ef4444, #b91c1c);
  box-shadow: 0 14px 28px rgba(239, 68, 68, 0.20);
}

.mgvoice-btn.soft,
.mgvoice-btn.ghost {
  color: #112238;
  background: #ffffff;
  border-color: var(--mg-border);
}

.mgvoice-btn.compact {
  min-width: 76px;
}

.mgvoice-alert {
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(245, 158, 11, 0.10);
  border: 1px solid rgba(245, 158, 11, 0.22);
  color: #92400e;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.45;
}

.mgvoice-chat-card {
  overflow: hidden;
  background: rgba(255,255,255,0.96);
}

.mgvoice-chat-window {
  min-height: 360px;
  max-height: 520px;
  overflow-y: auto;
  padding: 22px;
  background:
    radial-gradient(circle at 100% 0%, rgba(24, 183, 255, 0.08), transparent 32%),
    #f7fbff;
}

.mgvoice-msg {
  display: flex;
  margin-bottom: 12px;
}

.mgvoice-msg.assistant {
  justify-content: flex-start;
}

.mgvoice-msg.user {
  justify-content: flex-end;
}

.mgvoice-bubble {
  max-width: min(680px, 86%);
  padding: 13px 15px;
  border-radius: 18px;
  font-size: 14px;
  line-height: 1.55;
  font-weight: 750;
  box-shadow: 0 10px 24px rgba(7,17,31,0.06);
}

.mgvoice-msg.assistant .mgvoice-bubble {
  color: var(--mg-text);
  background: #ffffff;
  border: 1px solid #d9e8f6;
  border-bottom-left-radius: 6px;
}

.mgvoice-msg.user .mgvoice-bubble {
  color: #ffffff;
  background: linear-gradient(135deg, #1f7bc1, #0f5f9d);
  border-bottom-right-radius: 6px;
}

.mgvoice-bubble.listening {
  color: #0b4e84;
  background: rgba(31, 123, 193, 0.10);
  animation: mgvoiceListening 1.2s ease-in-out infinite;
}

.mgvoice-live-transcript {
  margin: 8px 0 14px;
  padding: 13px 15px;
  border-radius: 16px;
  background: rgba(24, 183, 255, 0.08);
  border: 1px solid rgba(24, 183, 255, 0.18);
}

.mgvoice-live-transcript span {
  display: block;
  margin-bottom: 5px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.mgvoice-live-transcript strong {
  color: var(--mg-text);
  font-size: 14px;
}

.mgvoice-input-row {
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 16px;
  border-top: 1px solid var(--mg-border);
  background: rgba(255,255,255,0.92);
  backdrop-filter: blur(14px);
}

.mgvoice-mic-btn {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  border: 1px solid rgba(31, 123, 193, 0.18);
  background: rgba(31, 123, 193, 0.10);
  color: #0b4e84;
  cursor: pointer;
  font-size: 20px;
  font-weight: 950;
}

.mgvoice-mic-btn.recording {
  color: #ffffff;
  background: linear-gradient(135deg, #ef4444, #b91c1c);
  animation: mgvoiceMicPulse 1.2s infinite;
}

.mgvoice-input-row textarea {
  width: 100%;
  min-height: 52px;
  max-height: 120px;
  padding: 14px;
  border-radius: 16px;
  border: 1px solid var(--mg-border);
  background: #f7fbff;
  color: var(--mg-text);
  font-size: 14px;
  font-weight: 750;
  outline: none;
  resize: vertical;
}

.mgvoice-input-row textarea:focus {
  background: #ffffff;
  border-color: #1f7bc1;
  box-shadow: 0 0 0 4px rgba(31, 123, 193, 0.12);
}

@keyframes mgvoiceFloat {
  0%, 100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-8px);
  }
}

@keyframes mgvoiceHalo {
  0%, 100% {
    opacity: 0.55;
    transform: scale(1);
  }

  50% {
    opacity: 0.9;
    transform: scale(1.06);
  }
}

@keyframes mgvoiceHaloActive {
  0%, 100% {
    opacity: 0.7;
    transform: scale(1);
  }

  50% {
    opacity: 1;
    transform: scale(1.14);
  }
}

@keyframes mgvoiceBlink {
  0%, 92%, 100% {
    transform: scaleY(1);
  }

  94% {
    transform: scaleY(0.12);
  }
}

@keyframes mgvoiceTalk {
  0%, 100% {
    height: 8px;
    width: 38px;
  }

  50% {
    height: 22px;
    width: 30px;
  }
}

@keyframes mgvoiceWave {
  0%, 100% {
    height: 16px;
    opacity: 0.55;
  }

  50% {
    height: 46px;
    opacity: 1;
  }
}

@keyframes mgvoiceListening {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(24, 183, 255, 0.18);
  }

  50% {
    box-shadow: 0 0 0 8px rgba(24, 183, 255, 0);
  }
}

@keyframes mgvoiceMicPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.28);
  }

  70% {
    box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
  }
}

@media (max-width: 1080px) {
  .mgvoice-hero {
    grid-template-columns: 1fr;
  }

  .mgvoice-avatar-card {
    min-height: 380px;
  }
}

@media (max-width: 720px) {
  .mgvoice-header {
    flex-direction: column;
  }

  .mgvoice-lang-select {
    width: 100%;
  }

  .mgvoice-actions {
    flex-direction: column;
  }

  .mgvoice-btn {
    width: 100%;
  }

  .mgvoice-input-row {
    grid-template-columns: 52px 1fr;
  }

  .mgvoice-input-row .mgvoice-btn.compact {
    grid-column: 1 / -1;
    width: 100%;
  }

  .mgvoice-avatar-card,
  .mgvoice-control-card {
    padding: 20px;
  }

  .mgvoice-chat-window {
    min-height: 320px;
    padding: 16px;
  }
}
`;